class ReviewMarqueeComponent extends HTMLElement {
  constructor() {
    super();

    this.viewport = null;
    this.track = null;
    this.baseGroup = null;

    this.rafId = null;
    this.resizeObserver = null;

    this.currentX = 0;
    this.lastTime = 0;
    this.groupWidth = 0;

    this.speed = 40;
    this.direction = 'left';
    this.currentRate = 1;
    this.targetRate = 1;
    this.hoverSlowdown = 0.35;
    this.pauseOnHover = false;

    this.isDragging = false;
    this.dragStartX = 0;
    this.dragStartTranslate = 0;
    this.pointerId = null;

    this.handleResize = this.handleResize.bind(this);
    this.handlePointerEnter = this.handlePointerEnter.bind(this);
    this.handlePointerLeave = this.handlePointerLeave.bind(this);
    this.handlePointerDown = this.handlePointerDown.bind(this);
    this.handlePointerMove = this.handlePointerMove.bind(this);
    this.handlePointerUp = this.handlePointerUp.bind(this);
    this.handlePointerCancel = this.handlePointerCancel.bind(this);
    this.animate = this.animate.bind(this);
  }

  connectedCallback() {
    this.viewport = this.querySelector('[data-review-marquee-viewport]');
    this.track = this.querySelector('[data-review-marquee-track]');
    this.baseGroup = this.querySelector('[data-review-marquee-base]');

    if (!this.viewport || !this.track || !this.baseGroup) return;

    this.speed = Number(this.dataset.speed || 40);
    this.direction = this.dataset.direction === 'right' ? 'right' : 'left';
    this.hoverSlowdown = Number(this.dataset.hoverSlowdown || 0.35);
    this.pauseOnHover = this.dataset.pauseOnHover === 'true';

    this.build();
    this.start();

    this.addEventListener('pointerenter', this.handlePointerEnter);
    this.addEventListener('pointerleave', this.handlePointerLeave);

    this.viewport.addEventListener('pointerdown', this.handlePointerDown);
    this.viewport.addEventListener('pointermove', this.handlePointerMove);
    this.viewport.addEventListener('pointerup', this.handlePointerUp);
    this.viewport.addEventListener('pointercancel', this.handlePointerCancel);
    this.viewport.addEventListener('lostpointercapture', this.handlePointerUp);

    window.addEventListener('resize', this.handleResize);

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => {
        this.handleResize();
      });
      this.resizeObserver.observe(this);
      this.resizeObserver.observe(this.baseGroup);
    }
  }

  disconnectedCallback() {
    this.stop();

    this.removeEventListener('pointerenter', this.handlePointerEnter);
    this.removeEventListener('pointerleave', this.handlePointerLeave);

    if (this.viewport) {
      this.viewport.removeEventListener('pointerdown', this.handlePointerDown);
      this.viewport.removeEventListener('pointermove', this.handlePointerMove);
      this.viewport.removeEventListener('pointerup', this.handlePointerUp);
      this.viewport.removeEventListener('pointercancel', this.handlePointerCancel);
      this.viewport.removeEventListener('lostpointercapture', this.handlePointerUp);
    }

    window.removeEventListener('resize', this.handleResize);

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }

  handlePointerEnter() {
    if (this.isDragging) return;
    this.targetRate = this.pauseOnHover ? 0 : this.hoverSlowdown;
  }

  handlePointerLeave() {
    if (this.isDragging) return;
    this.targetRate = 1;
  }

  handlePointerDown(event) {
    if (!this.viewport || !this.groupWidth) return;

    this.isDragging = true;
    this.pointerId = event.pointerId;
    this.dragStartX = event.clientX;
    this.dragStartTranslate = this.currentX;
    this.targetRate = 0;

    this.viewport.setPointerCapture?.(event.pointerId);
    this.classList.add('is-dragging');
  }

  handlePointerMove(event) {
    if (!this.isDragging || event.pointerId !== this.pointerId) return;

    const deltaX = event.clientX - this.dragStartX;
    this.currentX = this.dragStartTranslate + deltaX;
    this.normalizePosition();
    this.render();
  }

  handlePointerUp(event) {
    if (!this.isDragging) return;
    if (typeof event.pointerId !== 'undefined' && event.pointerId !== this.pointerId) return;

    this.isDragging = false;
    this.pointerId = null;
    this.classList.remove('is-dragging');

    this.targetRate = this.matches(':hover')
      ? (this.pauseOnHover ? 0 : this.hoverSlowdown)
      : 1;
  }

  handlePointerCancel(event) {
    this.handlePointerUp(event);
  }

  handleResize() {
    this.stop();
    this.build();
    this.start();
  }

  removeClones() {
    const clones = this.track.querySelectorAll('[data-review-marquee-clone="true"]');
    clones.forEach((clone) => clone.remove());
  }

  build() {
    this.removeClones();

    const viewportWidth = this.offsetWidth;
    const baseWidth = this.baseGroup.scrollWidth;

    if (!viewportWidth || !baseWidth) return;

    this.groupWidth = baseWidth;

    let totalWidth = baseWidth;
    let safety = 0;

    while (totalWidth < viewportWidth * 3 && safety < 20) {
      const clone = this.baseGroup.cloneNode(true);
      clone.setAttribute('data-review-marquee-clone', 'true');
      clone.setAttribute('aria-hidden', 'true');
      this.track.appendChild(clone);
      totalWidth += baseWidth;
      safety += 1;
    }

    this.currentX = 0;
    this.render();
  }

  start() {
    this.lastTime = performance.now();
    this.rafId = requestAnimationFrame(this.animate);
  }

  stop() {
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  normalizePosition() {
    if (!this.groupWidth) return;

    while (this.currentX <= -this.groupWidth) {
      this.currentX += this.groupWidth;
    }

    while (this.currentX >= this.groupWidth) {
      this.currentX -= this.groupWidth;
    }
  }

  render() {
    this.track.style.transform = `translate3d(${this.currentX}px, 0, 0)`;
  }

  animate(now) {
    const delta = (now - this.lastTime) / 1000;
    this.lastTime = now;

    this.currentRate += (this.targetRate - this.currentRate) * 0.08;

    if (!this.isDragging) {
      const velocity = this.speed * this.currentRate;
      const directionFactor = this.direction === 'right' ? 1 : -1;

      this.currentX += directionFactor * velocity * delta;
      this.normalizePosition();
      this.render();
    }

    this.rafId = requestAnimationFrame(this.animate);
  }
}

if (!customElements.get('review-marquee-component')) {
  customElements.define('review-marquee-component', ReviewMarqueeComponent);
}